import { Button, message, Tooltip } from 'antd';
import axios from 'axios';
import moment from 'moment';
import React, { createRef } from 'react';
import { Container, Modal } from 'react-bootstrap';
import { Check, Pencil, Trash } from 'react-bootstrap-icons';
import { withTranslation, WithTranslation } from 'react-i18next';
import { reactFormatter } from 'react-tabulator';
import Contact from '../../../../Model/models/contactModel';
import Location from '../../../../Model/models/locationModel';
import Order from '../../../../Model/models/orderModel';
import PaymentType from '../../../../Model/models/paymentTypeModel';
import OrderPayment from '../../../../Model/Payment';
import ProductType from '../../../../Model/productType';
import { OrderFormUtils } from '../../../../utils/orderFormUtils';
import TableView from '../../../tableView';
import PaymentForm from '../../Payments/PaymentForm';
import OrderFormBillingModal from './orderFormBillingModal';
import './orderFormBillingModal.scss';
import OrderFormItems from './orderFormItems';
import OrderFormShippings from './orderFormShippings';

interface OrderFormBodyProps extends WithTranslation {
  order: Order,
  productTypes: ProductType[],
  currencySymbol: string,
  paymentTypes: PaymentType[],
  orderUpdated: (order: Order) => void;
  paymentUpdated: (payments: OrderPayment[]) => void;
}
interface OrderFormBodyState {
  order: Order,
  toShipItems: number,
  shippeditems: number,
  refundedItems: number,
  editNote: boolean,
  editItems: boolean,
  subtotalRefund: number,
  vatRefund: number,
  currencySymbol: string,
  editBilling: boolean,
  showModal: boolean,
  generateInvoice?: 'none' | '30%' | '100%',
  payment?: OrderPayment,
  convertRatio?: number,
  showInvoiceModal: boolean,
  paymentData: OrderPayment[],
  currency?: string;
}


function PaymentActions(context: any) {
  // eslint-disable-next-line no-underscore-dangle
  const cellData = context.cell._cell.row.data;
  return <>
    <Tooltip title={context.props.t('DELETE_PAYMENT')}>
      <Button onClick={() => {
        // eslint-disable-next-line no-underscore-dangle
        context.props.deletePayments(cellData);
      }} style={{ marginRight: '1em', background: 'red', borderColor: 'red', color: 'white' }} color='danger'
        disabled={cellData.billingCode}
      >
        <Trash />
      </Button>
    </Tooltip>
  </>;
}

class OrderFormBody extends React.Component<OrderFormBodyProps, OrderFormBodyState> {

  paymentsTable = createRef<TableView>();

  constructor(props: OrderFormBodyProps) {
    super(props);
    this.state = {
      order: props.order,
      editItems: false,
      editNote: false,
      shippeditems: 0,
      refundedItems: 0,
      subtotalRefund: 0,
      toShipItems: 0,
      vatRefund: 0,
      currencySymbol: '',
      editBilling: false,
      showInvoiceModal: false,
      paymentData: [],
      showModal: false
    };
  }

  componentDidUpdate(nextProps: OrderFormBodyProps) {
    if (nextProps.productTypes.length !== this.props.productTypes.length) {
      const itemsInfo = this.getItemsInfo(nextProps.order);
      this.setState({
        toShipItems: itemsInfo.totalItemsToShip - itemsInfo.shipped,
        shippeditems: itemsInfo.shipped,
        subtotalRefund: itemsInfo.subtotalRefund,
        refundedItems: itemsInfo.refundedItems,
        vatRefund: itemsInfo.vatRefund
      });
    }
  }

  shouldComponentUpdate(nextProps: OrderFormBodyProps) {
    if (nextProps.currencySymbol !== this.state.currencySymbol) {
      this.setState({ currencySymbol: nextProps.currencySymbol });
    }
    if (nextProps.order !== this.state.order) {
      const itemsInfo = this.getItemsInfo(nextProps.order);
      this.setState({
        order: nextProps.order,
        toShipItems: itemsInfo.totalItemsToShip - itemsInfo.shipped,
        shippeditems: itemsInfo.shipped,
        subtotalRefund: itemsInfo.subtotalRefund,
        refundedItems: itemsInfo.refundedItems,
        vatRefund: itemsInfo.vatRefund
      });
    }
    return true;
  }

  openBillingModal = () => {
    this.setState({ editBilling: true });
  };

  updateOverview = (edit?: boolean) => {
    const { order } = this.props;

    const itemsInfo = this.getItemsInfo(order);

    this.state.order.total = itemsInfo.subtotal + itemsInfo.vat;
    this.state.order.vat = itemsInfo.vat;

    this.setState({
      toShipItems: itemsInfo.totalItemsToShip - itemsInfo.shipped,
      shippeditems: itemsInfo.shipped,
      subtotalRefund: itemsInfo.subtotalRefund,
      refundedItems: itemsInfo.refundedItems,
      vatRefund: itemsInfo.vatRefund,
      editItems: edit ?? this.state.editItems
    });
    this.orderUpdated(this.state.order);
  };

  getItemsInfo(order: Order) {
    const refundedItems = order.orderProducts
      .filter(op => ['refunded', 'partial_refund'].indexOf(op.status || 'completed') > -1)
      .map((op) => op.quantity)
      .reduce((a, b) => a + b, 0);
    const totalItemsToShip = order.orderProducts
      .filter(op => {
        const productType = this.props.productTypes.find(pt => pt.id === op.product_type_id);
        return ['refunded', 'partial_refund'].indexOf(op.status || 'completed') == -1 &&
          productType && !productType.code.startsWith('LL_');
      })
      .map((op) => op.quantity)
      .reduce((a, b) => a + b, 0);
    const shipped = (order.shippings || [])
      .map(s => s.quantity)
      .reduce((a: number, b: number) => a + b, 0);

    const subtotal = order.orderProducts
      .map((op) => op.price * op.quantity)
      .reduce((a, b) => a + b, 0);
    const vat = order.orderProducts
      .map((op) => op.vat)
      .reduce((a, b) => a + b, 0);

    const subtotalRefund = order.orderProducts
      .filter(op => op.status && op.status !== 'failed_refund')
      .map((op) => op.price * op.quantity)
      .reduce((a, b) => a + b, 0);
    const vatRefund = order.orderProducts
      .filter(op => op.status && op.status !== 'failed_refund')
      .map((op) => op.vat)
      .reduce((a, b) => a + b, 0);

    return {
      refundedItems,
      totalItemsToShip,
      shipped, subtotal,
      vat,
      subtotalRefund,
      vatRefund
    };
  }

  orderUpdated(order: Order) {
    this.setState({ order });
    this.props.orderUpdated(order);
  }

  onBillingSave = (contact: Contact, location: Location, company_id: number | undefined) => {
    const order = this.state.order;
    order.company_id = company_id;
    order.Billing = location;
    order.Billing.contact = contact;
    order.Billing.contact_id = contact.id;
    this.orderUpdated(this.state.order);
    this.updateOverview();
    this.setState({ order, editBilling: false });
  };

  openModal = () => {
    const order = this.state.order;
    if (order.currency) {
      this.setState({ showModal: true });
    } else {
      message.warning({
        content: this.props.t('SELECT_CURRENCY'),
        duration: 2.5,
        style: {
          marginTop: 100
        }
      });
    }
  };

  closeModal = () => this.setState({ showModal: false });

  // generateInvoice = (e: any) => {
  //   const payment = e.target.value === 'none' ? undefined : {
  //     amount: 0,
  //     billingCode: null,
  //     deadlineDate: DateTime.now().plus({ days: 14 }).toJSDate(),
  //     isFullBilling: null,
  //     orderId: this.state.order.id || 0,
  //     paymentTypeId: 0,
  //     percentage: e.target.value === '30%' ? 30 : 100,
  //     status: 'not_paid'
  //   };
  //   this.setState({
  //     generateInvoice: e.target.value,
  //     payment
  //   });
  //   this.props.paymentUpdated(e.target.value, payment);
  // };


  onChangePaymentMethod = async (value: any) => {
    const { paymentTypes } = this.props;
    const { payment, order } = this.state;
    const paymentType = paymentTypes.find(p => p.id === value);
    let convertRatio: number | undefined = 0;
    let currency: string | undefined = undefined;
    if (paymentType) {
      if (['PLANETS', 'USDT', 'USDC', 'ALGO'].indexOf(paymentType.currency) > -1) {
        let crypto: string | undefined = undefined;
        switch (paymentType.currency) {
          case 'PLANETS':
            crypto = 'planetwatch';
            currency = 'PLANETS';
            break;
          case 'USDT':
            crypto = 'tether';
            currency = 'USDT';
            break;
          case 'USDC':
            crypto = 'usd-coin';
            currency = 'USDC';
            break;
          case 'ALGO':
            crypto = 'algorand';
            currency = 'ALGO';
            break;
          default:
            break;
        }
        if (crypto && payment) {
          const convertData = await this.convertFiatIn(crypto, new Date());
          convertRatio = convertData[(order.currency || '').toLowerCase()];
          payment.amount = this.roundToNDecimals(order.total * (payment.percentage / 100) / convertData.eur);
        }
      } else {
        convertRatio = undefined;
        if (payment) {
          payment.amount = order.total * (payment.percentage / 100);
        }
        currency = order.currency;
      }
      if (payment) {
        payment.paymentTypeId = value;
      }
      this.setState({
        currency,
        payment,
        convertRatio
      });
      this.props.paymentUpdated(this.state.paymentData);
    }
  };

  roundToNDecimals = (amount: number, decimals: number = 6) => {
    const n = 10 ** decimals;
    return Math.round(amount * n) / n;
  };

  convertFiatIn = async (crypto: string, date: Date) => {
    const result = await axios.get(
      `https://api.coingecko.com/api/v3/coins/${crypto}/history?date=${moment(date).format('DD-MM-YYYY')}`
    );
    return result.data.market_data?.current_price;
  };

  deletePayments = (cellData: any) => {
    if (this.state.paymentData) {
      const index = this.state.paymentData.findIndex(item => item.id === cellData.id);
      const { paymentData } = this.state;
      paymentData.splice(index, 1);
      this.setState({ paymentData });
    }
  };

  private columnsPayments = [
    {
      title: this.props.t('DATE'),
      field: 'deadlineDate',
      sorter: 'string',
      formatter: (cell: any) => {
        const locale =
          (window.navigator as any).userLanguage || window.navigator.language;
        return moment(cell.getValue())
          .locale(locale)
          .format('DD/MM/YYYY HH:mm');
      }
    },
    {
      title: this.props.t('AMOUNT'),
      field: 'amount',
      sorter: 'string'
    },
    {
      title: this.props.t('PAYMENT_METHOD'),
      field: 'payment_method',
      sorter: 'string'
    },
    {
      title: this.props.t('CURRENCY'),
      field: 'currency',
      sorter: 'string'
    },
    {
      title: this.props.t('PERCENTAGE'),
      field: 'percentage',
      sorter: 'string',
      formatter: (cell: any) => `${cell.getValue()}%`
    },
    {
      title: this.props.t('STATUS'),
      field: 'status',
      sorter: 'string',
      formatter: (cell: any) => {
        if (cell.getValue() === 'not_paid') {
          return this.props.t('NOT_PAID');
        } else {
          return this.props.t('PAID');
        }
      }
    },
    {
      title: this.props.t('FULL_BILLING'),
      field: 'isFullBilling',
      sorter: 'string',
      formatter: (cell: any) =>
        cell.getValue() ? this.props.t('YES') : this.props.t('NO')
    },
    {
      title: this.props.t('NOTE'),
      field: 'note'
    },
    {
      title: '',
      sortable: false,
      formatter: reactFormatter(<PaymentActions props={{
        deletePayments: this.deletePayments.bind(this),
        t: this.props.t
      }} />),
      headerSort: false,
      width: '300'
    }
  ];

  render() {
    const location = this.state.order.Billing;
    const contact = this.state.order.Billing?.contact;
    // const { convertRatio, order, payment, currency } = this.state;
    // const { paymentTypes } = this.props;
    return <>
      <div className='fullTable shadow rounded'>
        <table className='orderTable'>
          <thead className='tableTop'>
            <tr>
              <th scope='col' className="billing">{this.props.t('BILLING')}</th>
              <th scope='col'>{this.props.t('ITEM')}</th>
              <th scope='col'>{this.props.t('QUANTITY')}</th>
              <th scope='col'>{this.props.t('COST')}</th>
              <th scope='col'>{this.props.t('PRICE')}</th>
              <th scope='col'>{this.props.t('VAT')}</th>
              <th scope='col'>{this.props.t('STATE')}</th>
              <th scope='col' className='btn-cell-add'>
                <div className='addButtonWrapper'>
                  <button className='addButton'
                    onClick={() => {
                      if (this.state.editItems) {
                        this.updateOverview(false);
                        return;
                      }
                      this.setState({ editItems: true });
                    }}
                  >
                    {this.state.editItems ? this.props.t('SAVE') : this.props.t('EDIT')}
                  </button>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <div className="d-inline position-absolute billing-td">
              {
                (contact?.name || contact?.surname) &&
                <>{contact?.name} {contact?.surname}<br /></>
              }
              {location?.address && <>{location?.address}<br /></>}
              {location?.city && <>{location?.city}<br /></>}
              {
                (location?.province || location?.postcode) &&
                <>{location?.province} {location?.postcode}<br /></>
              }
              {location?.country && <>{location?.country}<br /><br /></>}
              {/* country code should match a complete name */}
              <button className="iconButton my-2" onClick={() => {
                this.openBillingModal();
              }}>
                <Pencil />
              </button>
            </div>


            <OrderFormItems
              modalData={this.state.showModal}
              showModal={this.openModal}
              hideModal={this.closeModal}
              onItemsChange={(orderProducts) => {
                this.state.order.orderProducts = orderProducts;
                this.orderUpdated(this.state.order);
              }}
              currencySymbol={this.state.currencySymbol}
              order={this.state.order}
              productTypes={this.props.productTypes}
              updateOverview={this.updateOverview.bind(this)}
              editItems={this.state.editItems}
            />
            <tr>
              <td></td>
              <td colSpan={12} className="add-button" >
                {this.state.editItems &&
                  <button className='addButton'
                    onClick={() => {
                      this.openModal();
                    }}>{this.props.t('ADD_PRODUCT')}</button>}
              </td>
            </tr>

            <tr>

              <td></td>
              <td></td>
              <td>
                {this.props.t('ITEMS_TO_SHIP')}:  <br />
                {this.props.t('ITEMS_SHIPPED')}: <br />
                {this.props.t('ITEMS_REFUNDED')}: <br />
                {this.props.t('TOTAL_ITEMS')}:
              </td>
              <td>
                {this.state.toShipItems}<br />
                {this.state.shippeditems}<br />
                {this.state.refundedItems}<br />
                {this.state.order.orderProducts.map(op => op.quantity).reduce((a, b) => a + b, 0)}
              </td>
              <td>
                {this.props.t('ORDER_SUBTOTAL')}:<br />
                {this.props.t('VAT')}:<br />
                {this.props.t('ORDER_TOTAL')}:
              </td>
              <td className='font-weight-bold'>
                {OrderFormUtils.getPriceFormat(this.state.order.total - this.state.order.vat, this.state.currencySymbol)}
                <br></br>
                {OrderFormUtils.getPriceFormat(this.state.order.vat, this.state.currencySymbol)}
                <br></br>
                {OrderFormUtils.getPriceFormat(this.state.order.total, this.state.currencySymbol)}
              </td>
              <td>
                <span style={{ color: 'red' }}>
                  ({OrderFormUtils.getPriceFormat(this.state.subtotalRefund * -1, this.state.currencySymbol)})
                </span>
                <br></br>
                <span style={{ color: 'red' }}>
                  ({OrderFormUtils.getPriceFormat(this.state.vatRefund * -1, this.state.currencySymbol)})
                </span>
                <br></br>
                <span style={{ color: 'red' }}>
                  ({OrderFormUtils.getPriceFormat(
                  (this.state.subtotalRefund + this.state.vatRefund) * -1,
                  this.state.currencySymbol
                )})
                </span>
              </td>
            </tr>
            <tr>
              <td></td>
              <td></td>
              <td colSpan={6} style={{ whiteSpace: 'pre-line' }}>
                <b>Note:</b> {
                  !this.state.editNote
                    ? <>{this.state.order?.note}</>
                    : <textarea style={{ width: '100%' }} defaultValue={this.state.order?.note}
                      onChange={(ev: any) => {
                        this.state.order.note = ev.target.value;
                        this.setState({});
                      }}
                    ></textarea>
                }
                <button className="iconButton my-2" onClick={() => {
                  this.setState({ editNote: !this.state.editNote });
                }}>
                  {!this.state.editNote ? <Pencil /> : <Check />}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* SHIPPINGS TABLE */}
      <OrderFormShippings
        productTypes={this.props.productTypes}
        order={this.state.order}
        onChange={(o) => {
          this.updateOverview();
          this.orderUpdated(o);
        }}
      />

      {
        this.state.order.id === undefined &&
        <>
          <div className='invoice' style={{ backgroundColor: 'white', marginTop: 20, padding: 20 }}>
            <div style={{ marginBottom: '2em' }}>
              <Button
                type='primary'
                onClick={() => {
                  this.setState({ showInvoiceModal: true });
                }}
              >
                {this.props.t('ADD_PAYMENT')}
              </Button>
              <TableView
                t={this.props.t}
                ref={this.paymentsTable}
                id={'order_payments'}
                paginationSize={100}
                data={this.state.paymentData}
                //  api={constants.api.getOrderPayments.replace('{orderId}', params.orderId)}
                columns={this.columnsPayments}
              />

            </div>
          </div>

          <Modal
            show={this.state.showInvoiceModal}
            onHide={() => this.setState({ showInvoiceModal: false })}
            backdrop="static"
            keyboard={false}
            size="xl"
            centered={true}
          >
            <Modal.Header closeButton>{this.props.t('ADD_PAYMENT')}</Modal.Header>
            <Modal.Body>
              <Container>
                <PaymentForm
                  importedOrder={this.state.order}
                  payments={this.state.paymentData}
                  onPaymentSave={(payment: OrderPayment) => {
                    const { paymentData } = this.state;
                    paymentData?.push(payment);
                    this.setState({ paymentData });
                    this.setState({ showInvoiceModal: false });
                    this.paymentsTable.current?.reloadData();
                    this.props.paymentUpdated(paymentData);
                  }}
                  save={false}
                  closeModal={async () => {
                    this.setState({ showInvoiceModal: false });
                    this.paymentsTable.current?.reloadData();
                  }}
                ></PaymentForm>
              </Container>
            </Modal.Body>
          </Modal>

        </>
      }
      {
        this.state.editBilling &&
        <OrderFormBillingModal
          order={this.state.order}
          contact={this.state.order.Billing?.contact}
          location={this.state.order.Billing}
          onModalClose={() => this.setState({ editBilling: false })}
          onSave={this.onBillingSave.bind(this)}
        />
      }
    </>;
  }

}

export default withTranslation()(OrderFormBody);